import React, { useState, useEffect } from "react"
import {
  Bell,
  BarChart,
  FileText,
  Home,
  Mail,
  Search,
  Store,
  User,
  Palette,
  Menu,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import CommunitySidebar from "../comm/CommunitySidebar"
import { useCommunity } from "../../context/CommunityContext"
import useAuth from "../../hooks/useAuth"


export function CommunityDashboardLayout({
  children,
  communityId,
}) {
  // Add communityId to state management
  const [mounted, setMounted] = useState(false)
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true)
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileRightOpen, setIsMobileRightOpen] = useState(false)
  const pathname = useLocation().pathname;

  const communityCtx = useCommunity();
  const authCtx = useAuth();

  // Update the navigation array to use "updates" instead of "newsletter"
const navigation = () => [
  { name: "Dashboard", href: `/communities/${communityCtx.community.community.slug}/`, icon: Home },
  { name: "Home", href: `/communities/${communityCtx.community.community.slug}/home`, icon: Home },
  { name: "Posts", href: `/communities/${communityCtx.community.community.slug}/posts`, icon: FileText },
  { name: "Updates", href: `/communities/${communityCtx.community.community.slug}/updates`, icon: Mail }, // Changed from "Newsletter" to "Updates"
  { name: "Members", href: `/communities/${communityCtx.community.community.slug}/members`, icon: Users },
  // { name: "Analytics", href: `/communities/${communityCtx.community.community.slug}/analytics`, icon: BarChart },
  // { name: "AI Search", href: `/communities/${communityCtx.community.community.slug}/ai-search`, icon: Search },
  { name: "Store", href: `/communities/${communityCtx.community.community.slug}/store`, icon: Store },
  { name: "Site Builder", href: `/communities/${communityCtx.community.community.slug}/site-builder`, role: 'owner', icon: Palette },
]

  useEffect(() => {
    setMounted(true)
    const leftState = localStorage.getItem("leftSidebarOpen")
    const rightState = localStorage.getItem("rightSidebarOpen")
    setLeftSidebarOpen(leftState === null ? true : leftState === "true")
    setRightSidebarOpen(rightState === null ? true : rightState === "true")
  }, [])

  const toggleLeftSidebar = () => {
    const newState = !leftSidebarOpen
    setLeftSidebarOpen(newState)
    localStorage.setItem("leftSidebarOpen", String(newState))
  }

  const toggleRightSidebar = () => {
    const newState = !rightSidebarOpen
    setRightSidebarOpen(newState)
    localStorage.setItem("rightSidebarOpen", String(newState))
  }

  if (!mounted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  // You can use communityId to fetch and display community-specific data
  return (
    <div className="min-h-screen flex bg-gray-50" style={{height: "calc(100vh - 64px)", minHeight: "calc(100vh - 64px)"}}>
      {/* Mobile overlay */}
      {(isMobileMenuOpen || isMobileRightOpen) && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => {
            setIsMobileMenuOpen(false)
            setIsMobileRightOpen(false)
          }}
        />
      )}

      {/* Left Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-40
          flex flex-col
          bg-white 
          border-r border-gray-200
          transition-all duration-300 ease-in-out
          ${leftSidebarOpen ? "w-[280px]" : "w-[80px]"}
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Collapse button */}
        <button
          onClick={toggleLeftSidebar}
          className="hidden lg:flex absolute -right-4 top-8 z-50 w-8 h-8 bg-white border border-gray-200 rounded-full items-center justify-center hover:bg-gray-50 transition-colors"
        >
          {leftSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>

        <div className="flex-1 flex flex-col min-h-0">

          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {navigation().map((item) => {
                const isActive = pathname === item.href;
                let allow = true;
                if(item.role && item.role !== communityCtx.community.userRole) {
                  allow = false;
                }
                if(allow) {
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg
                        transition-all duration-200 group
                        ${
                          isActive
                            ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/20"
                            : "hover:bg-gray-100 text-gray-700"
                        }
                      `}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <item.icon
                        className={`w-5 h-5 flex-shrink-0 ${
                          isActive
                            ? "text-white"
                            : "text-gray-500 group-hover:text-gray-700"
                        }`}
                      />
                      <span className={`font-medium truncate ${!leftSidebarOpen && "lg:hidden"}`}>{item.name}</span>
                    </Link>
                  )
                }
                else
                {
                  return (<React.Fragment key={item.name} />)
                }
              })}
            </div>
          </nav>

          {/* <div className="p-4 border-t border-gray-200">
            <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              {leftSidebarOpen && (
                <div className="flex-1 text-left lg:block hidden">
                  <p className="font-medium text-sm">{authCtx.user.name}</p>
                  <p className="text-xs text-gray-500">{authCtx.user.email}</p>
                </div>
              )}
            </button>
          </div> */}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 h-16 bg-white/80 backdrop-blur-sm border-b border-gray-200">
          <div className="h-full px-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* <div className="relative max-w-md w-full hidden sm:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="search"
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div> */}
            </div>

            <div className="flex items-center gap-3">
              {/* Community button - shows/hides right sidebar on mobile */}
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setIsMobileRightOpen(!isMobileRightOpen)}
              >
                <Users className="w-6 h-6" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <div className="px-4 py-6 max-w-[2000px] mx-auto w-full">{children}</div>
        </main>
      </div>

      {/* Right Sidebar */}
      <CommunitySidebar
        isOpen={rightSidebarOpen}
        isMobileOpen={isMobileRightOpen}
        onMobileClose={() => setIsMobileRightOpen(false)}
        onToggle={toggleRightSidebar}
      />
    </div>
  )
}

